import { markNotificationRead } from "./notification-center";
import { navigate } from "./navigation";
import { isSystemNotificationOpenMessage } from "./system-notifications";

const updateCheckIntervalMs = 30 * 60_000;
const updateCheckDebounceMs = 15 * 60_000;

let registration: ServiceWorkerRegistration | undefined;
let updateReadyAt: number | undefined;
let reloading = false;
let lastUpdateCheck = 0;
const listeners = new Set<() => void>();

export function serviceWorkerUpdateReady(): boolean {
  return updateReadyAt !== undefined;
}

export function serviceWorkerUpdateReadyAt(): number | undefined {
  return updateReadyAt;
}

export function subscribeServiceWorkerUpdate(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function announce(): void {
  updateReadyAt ??= Date.now();
  for (const listener of listeners) listener();
}

// The new worker precaches and then waits. Activating it mid-session would swap the asset manifest
// under a page still holding references to the chunks it loaded with, so the reload is the user's.
export function applyServiceWorkerUpdate(): void {
  registration?.waiting?.postMessage({ type: "SKIP_WAITING" });
}

function watchInstalling(worker: ServiceWorker | null): void {
  if (!worker) return;
  worker.addEventListener("statechange", () => {
    // Without an existing controller this is the very first install, not an update.
    if (worker.state === "installed" && navigator.serviceWorker.controller) announce();
  });
}

function checkForUpdate(): void {
  const now = Date.now();
  if (now - lastUpdateCheck < updateCheckDebounceMs) return;
  lastUpdateCheck = now;
  void registration?.update().catch(() => undefined);
}

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (!isSystemNotificationOpenMessage(event.data)) return;
    markNotificationRead(event.data.id);
    if (event.data.href.startsWith("/")) navigate(event.data.href);
    else window.open(event.data.href, "_blank", "noopener");
  });

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  void navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" })
    .then((registered) => {
      registration = registered;
      if (registered.waiting && navigator.serviceWorker.controller) announce();
      watchInstalling(registered.installing);
      registered.addEventListener("updatefound", () => watchInstalling(registered.installing));

      lastUpdateCheck = Date.now();
      window.setInterval(checkForUpdate, updateCheckIntervalMs);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") checkForUpdate();
      });
    })
    .catch(() => undefined);
}
